import { LoyaltyServiceOfferStoreRestrictionMode } from 'generated/graphql-gateway';
import { t } from 'state/intl/translations';
import { IncentiveEvaluationErrorCodes } from 'state/loyalty/hooks/types';

import { isStoreAllowed } from './is-store-allowed';
import type { OfferStoreRestrictionMode } from './is-store-allowed';

type OfferWithStoreRestriction = {
  inlineAlertMessage?: string | null;
  storeRestrictionMode?: OfferStoreRestrictionMode;
  availableStoreIds?: readonly (string | null | undefined)[] | null;
  errors?: ReadonlyArray<{ code?: string | null } | null> | null;
};

function hasStoreUnavailableError(errors: OfferWithStoreRestriction['errors']): boolean {
  return Boolean(
    errors?.some(
      error =>
        error?.code === IncentiveEvaluationErrorCodes.INVALID_STORE_ID ||
        error?.code === IncentiveEvaluationErrorCodes.NOT_AVAILABLE_IN_STORE
    )
  );
}

/**
 * Ensures store-restricted offers carry an inlineAlertMessage so offer detail can
 * redirect to the feed chrome and list cards can show unavailable UI.
 *
 * Prefers an existing BE message; otherwise applies FE include/exclude rules and
 * known store-unavailable evaluation error codes.
 */
export function applyStoreRestrictionUnavailableMessage<T extends OfferWithStoreRestriction>(
  offer: T,
  storeId: string | null | undefined
): T {
  if (offer.inlineAlertMessage || !storeId) {
    return offer;
  }

  const disallowedByMode =
    offer.storeRestrictionMode != null &&
    offer.storeRestrictionMode !== LoyaltyServiceOfferStoreRestrictionMode.ALL_STORES &&
    !isStoreAllowed(storeId, offer.storeRestrictionMode, offer.availableStoreIds);

  if (!disallowedByMode && !hasStoreUnavailableError(offer.errors)) {
    return offer;
  }

  return {
    ...offer,
    inlineAlertMessage: t('incentiveFeedbackInvalidStore', { incentiveType: t('offer') }),
  };
}
